import { Component } from '@angular/core';
import { AbstractWidgetDirective } from '../../abstract-widget.directive';
import { ActionsSubject, Store } from '@ngrx/store';
import { QueryService } from '@pt/api-client';
import { IoniconsOutline, UiWidget, WidgetConfiguration } from '@pt/pt-models';
import { FormControl, FormGroup } from '@angular/forms';
import { WidgetSimpleRadioButtonPreviewV0 } from './models/widget-simple-radio-button-preview-v0';
import { WidgetSimpleRadioButtonResponseV0 } from './models/widget-simple-radio-button-response-v0';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { WidgetDialogService } from '../../../services/widget-dialog.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { ValidatorGenerator } from '../../../utils/validator-generator';

@Component({
  selector: 'pt-widget-simple-radiobutton-v0',
  template: `
    <div class='position-relative' *ngIf="internalStatus.enabled">

      <pt-widget-disabled
        *ngIf='internalStatus.isDisabled'
        [showLoader]="internalStatus.viewMode === 'LOADING'">
      </pt-widget-disabled>

      <pt-widget-header
        class='w-100'
        [label]='widget?.label'
        [help]='widget?.help'

        [comment]="comment"
        [unsavedComment]="unsavedComment"
        [showCancelButton]='internalStatus.showCancelButton'
        [showCommentButton]='internalStatus.showCommentButton'
        [showEditButton]='internalStatus.showEditButton'
        [showReloadButton]='internalStatus.showReloadButton'
        [showSaveButton]='internalStatus.showSaveButton'
        [showOptButton]='internalStatus.showOptButton'

        [disabledCancelButton]='internalStatus.disabledCancelButton'
        [disabledCommentButton]='internalStatus.disabledCommentButton'
        [disabledEditButton]='internalStatus.disabledEditButton'
        [disabledOptButton]='internalStatus.disabledOptButton'
        [disabledReloadButton]='internalStatus.disabledReloadButton'
        [disabledSaveButton]='internalStatus.disabledSaveButton'
        [loading]='internalStatus.isLoading'
        (clickSaveWidget)='saveWidget()'
        (clickResetWidget)='resetWidget(widget)'
        (clickEditWidget)='dispatchEditState()'
        (clickReloadWidget)='reloadWidget(widget)'
        (clickCreateOpt)='createOptWidget()'
        (clickCommentWidget)='clickCommentWidget()'>
      </pt-widget-header>

      <ng-container *ngIf='!internalStatus.showOptButton'>
        <div
          class='radio-button-form-v0'
          [ngClass]="{'d-none' : internalStatus.viewMode !== 'EDIT' && internalStatus.viewMode !== 'LOADING' }">
          <form [formGroup]='formGroup'>
            <div class='form-check' *ngFor='let o of options'
                 (click)='setFormValue(o.id); $event.stopPropagation()'>
              <input class='form-check-input cursor-pointer'
                     type='radio'
                     formControlName='value'
                     [value]='o.id'
                     [id]="widget.internalId + '_radio_' + o.id">
              <label class='form-check-label'
                     [htmlFor]="widget.internalId + '_radio_' + o.id">
                {{ o.label }}
                <pt-w-help [help]='o?.help'></pt-w-help>
              </label>
            </div>
          </form>
        </div>

        <pt-widget-simple-radiobutton-v0-preview
          *ngIf="internalStatus.viewMode === 'PREVIEW'"
          [noDataLabel]="noDataLabel"
          [previewData]='previewData'
          [loading]='internalStatus.isLoading'>
        </pt-widget-simple-radiobutton-v0-preview>

        <pt-widget-error
          *ngIf="internalStatus.viewMode === 'ERROR'"
          [errorMessage]='internalStatus.errorMessage | async'>
        </pt-widget-error>

        <pt-widget-footer></pt-widget-footer>
      </ng-container>
    </div>
  `,
  styles: [`
    .form-check {
      width: 100%;
      height: 24px;
      display: flex;
      padding: 0 10px;
      cursor: pointer;
      align-items: center;
      margin-bottom: 1px;
    }

    .form-check:hover {
      background: #D6E4FF !important;
    }

    .form-check input {
      margin: 0;
      cursor: pointer;
    }

    .form-check .form-check-label {
      margin-left: 8px;
      cursor: pointer;
    }

    .radio-button-form-v0 {
      margin: 8px 8px;
    }
  `]
})
export class WidgetSimpleRadiobuttonV0Component extends AbstractWidgetDirective<WidgetSimpleRadioButtonPreviewV0> {
  public formGroup: FormGroup;
  options: WidgetSimpleRadioButtonResponseV0[];
  IconCheck = IoniconsOutline.check;
  IconClose = IoniconsOutline.close;
  private noBodyOptionLabel = this.translateService.instant('LABELS.NO_ONE');

  constructor(public store: Store,
              public actionsSubject: ActionsSubject,
              public translateService: TranslateService,
              public widgetDialogService: WidgetDialogService,
              public service: QueryService,
              public permissionService: NgxPermissionsService) {
    super(store, actionsSubject, translateService, widgetDialogService, permissionService);
    this.translateService.stream('WIDGET_WARNING.NOBODY').subscribe((success) => {
      this.noBodyOptionLabel = success;
    });
  }

  beforeOnInit(): void {
    this.initFormGroupIfNull();
  }


  setFormValue(value) {
    if (this.formGroup) {
      if (this.testId.includes(this.widget.internalId)) {
        console.log('[TEST] change form value ', value);
      }
      this.formGroup.controls.value.setValue(value);
    }
  }

  loadWidget(widget: UiWidget): void {
    this.initFormGroupIfNull();
    this.dispatchInitState();
    const value = widget.value;
    let preview = null;
    if (widget?.args?.data?.values?.length > 0) {
      this.options = widget?.args?.data ? widget.args.data.values as WidgetSimpleRadioButtonResponseV0[] : [];
      const radioValue = this.options?.find((o) => o.id === value);


      if (!this.properties.skipNullValue) {
        this.options = [{ id: -1, label: this.noBodyOptionLabel, help: null }, ...this.options];
      }


      if (radioValue) {
        preview = radioValue;
        if (this.testId.includes(this.widget.internalId)) {
          console.log('[TEST] load widget set value ', radioValue.id);
        }
        this.formGroup.controls.value.setValue(radioValue.id, { emitEvent: false, onlySelf: true });
      } else {
        preview = this.options[0];
        if (this.testId.includes(this.widget.internalId)) {
          console.log('[TEST] load widget set value ', -1);
        }
        this.formGroup.controls.value.setValue(-1, { emitEvent: false, onlySelf: true });
      }
    }
    this.formGroup.updateValueAndValidity({emitEvent: false, onlySelf: true});
    this.valueController(widget, this.options);
    this.checkValidity();
    setTimeout(() => this.dispatchCompleteState(preview));
  }

  saveWidget(ignoreTrigger: boolean = false): void {
    if (this.testId.includes(this.widget.internalId)) {
      console.log('[TEST] Save widget ignoreTrigger ', ignoreTrigger);
    }
    const widget = JSON.parse(JSON.stringify(this.widget)) as UiWidget;
    const id = this.formGroup?.value?.value;
    const selected = this.options.find((f) => f.id === id);
    widget.value = selected && selected.id >= 0 ? selected.id : undefined;
    if (this.testId.includes(this.widget.internalId)) {
      console.log('[TEST] Save widget value', widget.value);
    }
    this.checkValidity();
    this.completeSaveWidget(widget, ignoreTrigger);
  }

  configuration(): WidgetConfiguration {
    return { supportAutoSave: true, supportValidation: false };
  }

  private initFormGroupIfNull() {
    if (!this.formGroup && this.widget) {
      const formGroupValidators = ValidatorGenerator.generateFromGroupValidator(this.widget?.validators);
      const validators = ValidatorGenerator.generateValidator(this.widget?.validators);
      const asyncValidators = ValidatorGenerator.generateAsyncValidator(this.widget?.validators);
      this.formGroup = new FormGroup({
        value: new FormControl(null, validators, asyncValidators),
        comment: new FormControl()
      }, { validators: formGroupValidators });

      this.formGroup.valueChanges.pipe(
        takeUntil(this.destroy$)
      ).subscribe((valueChanges: { value: string }) => {
        this.checkValidity();
        if (this.internalStatus.isAutoSave && !this.internalStatus.isLoading && this.widget.value !== valueChanges.value && this.internalStatus.isDraft) {
          if (this.testId.includes(this.widget.internalId)) {
            console.log('[TEST] change form value ', valueChanges);
          }
          this.saveWidget();
        }
      });

      this.formGroup.updateValueAndValidity();
      this.checkValidity();
    }
  }

  private checkValidity() {
    switch (true) {
      case this.widget?.opt:
        this.isValid$.next(true);
        break;

      default:
        this.isValid$.next(this.formGroup?.valid);
    }
  }

  private valueController(widget: UiWidget, data: WidgetSimpleRadioButtonResponseV0[]) {

    const noData = !data || data?.length === 0;
    if (noData) {
      this.sendWidgetNotificationInvalidQueryResponseNoData(data);
    }

    const widgetValue: number = widget?.value ? widget?.value : undefined;
    const validValue = data?.map((d) => d.id)?.find((d) => d === widgetValue);
    if (validValue !== widgetValue) {
      const updatedWidget = JSON.parse(JSON.stringify(widget));
      updatedWidget.value = validValue;
      this.sendWidgetNotificationInvalidQueryResponseInvalidValue(updatedWidget);
    }


  }
}

